import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, Users } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { joinGame, leaveGame, startGame, PlayerInfo } from '../services/api';
import Avatar from '../components/Avatar';
import DisclaimerDialog from '../components/DisclaimerDialog';

interface LocationState {
  challengeFailed?: boolean;
  failedLevel?: number;
  showDisclaimer?: boolean;
}

const LobbyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const playerId = localStorage.getItem('playerId') || '';

  const { isConnected, gameState, error: wsError, setPlayerId } = useSocket();

  useEffect(() => {
    if (!playerId) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isConnected) {
      setPlayerId(playerId);
    }
  }, [playerId, isConnected, setPlayerId, navigate]);

  const [isLoading, setIsLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [failedLevel, setFailedLevel] = useState<number>(1);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Show error modal when actionError or wsError occurs
  useEffect(() => {
    if (actionError || wsError) {
      setShowErrorModal(true);
    }
  }, [actionError, wsError]);

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setActionError(null);
  };

  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.challengeFailed && state?.failedLevel) {
      setFailedLevel(state.failedLevel);
      setShowFailedModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // Check URL query params for showDisclaimer (from full page navigation)
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('showDisclaimer') === 'true') {
      setTimeout(() => setShowDisclaimer(true), 100);
      // Remove query param from URL
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const myPlayer = gameState?.players?.find(p => p.player_id === playerId);
  const canStart = gameState?.can_start && myPlayer != null;
  const isGameInProgress = gameState?.status === 'playing';

  const handleJoinGame = async (seatIndex: number) => {
    if (isLoading || myPlayer || isGameInProgress) return;
    setIsLoading(true);
    setActionError(null);
    try {
      const result = await joinGame(playerId, seatIndex);
      if (!result.success) {
        setActionError(result.error || 'Failed to join game');
      }
    } catch (e) {
      setActionError('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveGame = async () => {
    if (isLoading || !myPlayer) return;
    setIsLoading(true);
    setActionError(null);
    try {
      const result = await leaveGame(playerId);
      if (!result.success) {
        setActionError(result.error || 'Failed to leave game');
      }
    } catch (e) {
      setActionError('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartGame = async () => {
    if (isLoading || !canStart) return;
    setIsLoading(true);
    setActionError(null);
    try {
      const result = await startGame(playerId, 1);
      if (!result.success) {
        setActionError(result.error || 'Failed to start game');
      }
    } catch (e) {
      setActionError('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'sit_down': return 'sit down';
      case 'stand_up': return 'stand up';
      case 'playing': return 'In Game';
      default: return status;
    }
  };

  // Get room status display
  const getRoomStatusDisplay = () => {
    if (!isConnected) {
      return { status: 'Disconnected', color: 'red' };
    }
    switch (gameState?.status) {
      case 'waiting':
        return { status: 'Open', color: 'yellow' };
      case 'playing':
        return { status: 'In Game', color: 'green' };
      default:
        return { status: 'Connecting...', color: 'gray' };
    }
  };

  const roomStatus = getRoomStatusDisplay();

  const getStatusDotColor = (color: string) => {
    switch (color) {
      case 'yellow': return 'bg-yellow-400';
      case 'green': return 'bg-green-400';
      case 'red': return 'bg-red-400';
      default: return 'bg-gray-400';
    }
  };

  const renderPlayerRow = (seatIndex: number, player?: PlayerInfo) => {
    const isEmpty = !player;
    const isMySlot = player?.player_id === playerId;
    const canJoin = isEmpty && !isGameInProgress && !myPlayer;
    const isDisconnected = player && !player.connected;

    return (
      <div
        key={seatIndex}
        className={`flex items-center gap-4 py-2 ${canJoin ? 'cursor-pointer active:opacity-80' : ''}`}
        onClick={() => canJoin && handleJoinGame(seatIndex)}
      >
        {/* Avatar - pill shape container like Figma, always shows avatar */}
        <div className={`w-[72px] h-[50px] bg-white rounded-[50px] flex items-center justify-center overflow-hidden relative ${isEmpty ? 'opacity-50' : ''}`}>
          <Avatar seatIndex={seatIndex} size={40} />
          {isDisconnected && (
            <span className="absolute -bottom-1 -right-1 text-xs">💤</span>
          )}
        </div>

        {/* Player ID */}
        <div className="flex-1 flex flex-col">
          <span className="font-serif text-xl text-white font-bold">
            {player?.player_id || 'Empty Seat'}
          </span>
          {isEmpty && !isGameInProgress && (
            <span className="text-xs text-white/50">Tap to join</span>
          )}
        </div>

        {/* Status Button */}
        {player ? (
          <button
            className={`bg-[#8EA6C0] text-white font-serif text-sm py-2 px-5 rounded-full border-none ${
              isMySlot ? 'cursor-pointer active:opacity-80' : 'cursor-default'
            }`}
                        onClick={(e) => {
              if (isMySlot) {
                e.stopPropagation();
                handleLeaveGame();
              }
            }}
          >
            {isMySlot ? 'Leave' : getStatusLabel(player.status)}
          </button>
        ) : (
          <button
            className={`bg-[#8EA6C0] text-white font-serif text-sm py-2 px-5 rounded-full border-none ${
              canJoin ? 'cursor-pointer active:opacity-80' : 'opacity-50 cursor-not-allowed'
            }`}
                        disabled={!canJoin}
          >
            {isGameInProgress ? 'In Game' : 'Join'}
          </button>
        )}
      </div>
    );
  };

  const renderPlayerList = () => {
    const maxPlayers = gameState?.max_players || 8;
    const players = gameState?.players || [];
    const rows = [];
    for (let seatIndex = 0; seatIndex < maxPlayers; seatIndex++) {
      const player = players.find(p => p.seat_index === seatIndex);
      rows.push(renderPlayerRow(seatIndex, player));
    }
    return rows;
  };

  return (
    <div className="page bg-[#3C4959] p-4 pt-[calc(16px+env(safe-area-inset-top,0px))]">
      {/* Top Bar - Profile | Online Count */}
      <div className="flex items-center justify-between gap-2 mb-3">
        {/* Profile (Settings + Player ID) */}
        <button
          className="bg-white rounded-full h-[32px] px-3 flex items-center gap-2 cursor-pointer border-none"
          onClick={() => navigate('/settings')}
        >
          <Settings size={14} className="text-[#3C4959]" />
          <span className="font-serif text-sm text-[#3C4959] max-w-[80px] truncate font-bold">
            {playerId}
          </span>
        </button>

        {/* People in Room */}
        <div className="bg-white rounded-full h-[32px] px-3 flex items-center gap-2">
          <Users size={14} className="text-[#3C4959]" />
          <span className="font-serif text-sm text-[#3C4959] font-bold">
            {gameState?.online_count || 0}
          </span>
        </div>
      </div>

      {/* Room Status */}
      <div className="flex items-center justify-center gap-2 py-2 mb-2 border-b border-white/20">
        <span className={`w-2 h-2 rounded-full animate-pulse ${getStatusDotColor(roomStatus.color)}`} />
        <span className="font-serif text-sm text-white/80 font-bold">{roomStatus.status}</span>
        {!isGameInProgress && (gameState?.players_needed_to_start || 0) > 0 && (
          <span className="font-serif text-sm text-white/60 font-bold">· Need {gameState?.players_needed_to_start} more</span>
        )}
        {!isGameInProgress && (gameState?.players_needed_to_start || 0) === 0 && (gameState?.player_count || 0) > 0 && (
          <span className="font-serif text-sm text-white/60 font-bold">· Ready to start!</span>
        )}
      </div>

      {/* Player List */}
      <div className="flex-1 overflow-y-auto pb-24">
        {renderPlayerList()}
      </div>

      {/* Challenge Failed Modal */}
      {showFailedModal && (
        <div className="fixed inset-0 bg-[#3C4959] flex items-center justify-center p-5 z-[9999]">
          <div className="bg-white border border-black/25 rounded-[48px] py-8 px-6 w-full max-w-[320px] text-center">
            <div className="text-5xl mb-4">😢</div>
            <h2 className="font-serif text-xl text-black mb-2 font-bold">Mission Failed</h2>
            <p className="font-serif text-base text-black mb-6 font-bold">
              You didn't complete Level {failedLevel}
            </p>
            <button
              className="bg-[#8EA6C0] text-white py-3 px-10 rounded-full font-serif text-lg border-none cursor-pointer"
                            onClick={() => setShowFailedModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (actionError || wsError) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[9999]">
          <div className="bg-white border border-black/25 rounded-[48px] py-8 px-6 w-full max-w-[320px] text-center shadow-xl">
            <h2 className="font-serif text-xl text-black m-0 mb-5 font-bold">
              Error
            </h2>
            <p className="font-serif text-sm text-black mb-6 font-bold">
              {actionError || wsError}
            </p>
            <button
              className="bg-[#8EA6C0] text-white py-4 px-14 border-none rounded-full font-serif text-xl cursor-pointer transition-all active:scale-[0.96] active:opacity-90"
                            onClick={handleCloseErrorModal}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Disclaimer Dialog */}
      <DisclaimerDialog
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
      />

      {/* Fixed Bottom Start Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
        <button
          className={`w-full bg-[#32BD5A] text-white py-4 rounded-full font-serif text-xl font-bold border-none transition-all
            ${canStart ? 'cursor-pointer active:scale-[0.98] active:opacity-90' : 'opacity-50 cursor-not-allowed'}`}
          onClick={handleStartGame}
          disabled={!canStart || isLoading}
        >
          {isLoading ? '...' : 'Start'}
        </button>
      </div>
    </div>
  );
};

export default LobbyPage;
